<script setup>
import { ref, onMounted, onBeforeUnmount, reactive } from 'vue'
import { ElMessage } from 'element-plus'
import * as dashjsModule from 'dashjs'
import apiClient from '@/components/plugins/axios'

// 获取 dashjs，兼容不同的导出方式
const dashjs = dashjsModule.default || dashjsModule

const videoRef = ref(null)
const playerRef = ref(null)
const videoUrl = ref('https://wiseagent.oss-cn-shanghai.aliyuncs.com/website/upload/video/6761009c3a8d41e69ad23c17b2d23f3d/manifest.mpd')
const isInitializing = ref(false)
const retryCount = ref(0)
const maxRetries = 3
const uploading = ref(false)
const uploadProgress = ref(0)

// 表单数据
const formData = reactive({
  dashVideoId: null
})

// 全局错误处理函数引用
let globalErrorHandler = null
let globalRejectionHandler = null

// 立即设置全局错误处理，在组件挂载前就生效
globalErrorHandler = (event) => {
  const errorMessage = event.message || event.error?.message || ''
  const errorSource = event.filename || event.source || ''

  // 捕获 Script error 和跨域错误
  if (errorMessage.includes('Script error') ||
      errorMessage.includes('script error') ||
      errorMessage === '' ||
      (errorSource && !errorSource.includes(window.location.origin) && errorSource !== '')) {
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
    return true
  }

  // 捕获 video 相关错误
  if (event.target && (event.target.tagName === 'VIDEO' || event.target.tagName === 'SOURCE')) {
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
    return true
  }

  return false
}

globalRejectionHandler = (event) => {
  const reason = event.reason?.message || event.reason?.toString() || ''
  if (reason.includes('Script error') ||
      reason.includes('script error') ||
      reason === '') {
    event.preventDefault()
    event.stopPropagation()
    return true
  }
  return false
}

// 立即注册全局错误监听
window.addEventListener('error', globalErrorHandler, true)
window.addEventListener('unhandledrejection', globalRejectionHandler)

// 上传视频
async function handleUpload() {
  // 创建文件输入元素
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = 'video/*'

  input.onchange = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    uploading.value = true
    uploadProgress.value = 0

    try {
      const formDataToSend = new FormData()
      formDataToSend.append('file', file)

      const response = await apiClient.post('/video/upload', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            uploadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          }
        }
      })

      if (response.data.code === 200) {
        const data = response.data.data
        // 将 videoId 填入 formData
        formData.dashVideoId = data.videoId
        // 更新视频 URL 并重新初始化播放器
        videoUrl.value = data.mpdUrl

        ElMessage({
          message: '视频上传成功',
          type: 'success',
          duration: 2000
        })

        // 重新初始化播放器以播放新上传的视频
        setTimeout(() => {
          initPlayer()
        }, 500)
      } else {
        throw new Error(response.data.message || '上传失败')
      }
    } catch (error) {
      ElMessage({
        message: error.response?.data?.message || error.message || '上传失败，请稍后重试',
        type: 'error',
        duration: 5000
      })
    } finally {
      uploading.value = false
      uploadProgress.value = 0
    }
  }

  input.click()
}

// 初始化播放器
function initPlayer() {
  if (!videoRef.value) {
    return false
  }

  // 防止重复初始化
  if (isInitializing.value) {
    return false
  }

  isInitializing.value = true

  try {
    // 如果已有播放器实例，先销毁
    if (playerRef.value) {
      try {
        playerRef.value.destroy()
      } catch (e) {
        // 忽略销毁错误
      }
      playerRef.value = null
    }

    // 检查 dashjs 是否可用
    if (!dashjs) {
      throw new Error('dashjs 未正确导入')
    }

    // 尝试不同的方式访问 MediaPlayer
    let MediaPlayer
    if (dashjs.MediaPlayer) {
      MediaPlayer = dashjs.MediaPlayer
    } else if (dashjs.default && dashjs.default.MediaPlayer) {
      MediaPlayer = dashjs.default.MediaPlayer
    } else {
      throw new Error('无法找到 dashjs.MediaPlayer，请检查 dashjs 库是否正确安装')
    }

    // 创建 dashjs 播放器实例
    const player = MediaPlayer().create()

    playerRef.value = player

    // 配置播放器设置（dashjs 5.x 的配置方式可能不同，如果失败可忽略）
    try {
      player.getSettings()
    } catch (e) {
      // 配置失败不影响播放器使用
    }

    // 监听网络错误
    player.on('networkError', (data) => {
      ElMessage({
        message: `网络错误: ${data.error?.message || '无法加载视频片段，请检查网络连接或 CORS 配置'}`,
        type: 'error',
        duration: 5000
      })
    })

    // 监听错误事件
    player.on('error', (error) => {
      let errorMessage = '视频播放出错'

      if (error.error) {
        const mediaError = error.error

        if (mediaError.code === 4) {
          // MEDIA_ERR_SRC_NOT_SUPPORTED
          if (mediaError.message && mediaError.message.includes('audio object type')) {
            errorMessage = '音频编解码器不匹配：音频对象类型与 MIME 类型中指定的不匹配。这是后端编码问题，需要重新生成 DASH 文件，确保音频编码参数正确。'
          } else if (mediaError.message && mediaError.message.includes('codec')) {
            errorMessage = '编解码器不匹配：init.mp4 和 segment.m4s 的编解码器参数不一致。需要重新生成 DASH 文件，确保编解码器信息一致。'
          } else {
            errorMessage = '视频格式不支持或 CORS 跨域问题，请检查服务器 CORS 配置'
          }
        } else if (mediaError.code === 3) {
          errorMessage = '视频解码错误，可能是编解码器不匹配'
        } else {
          errorMessage = `视频播放错误 (错误代码: ${mediaError.code})`
        }
      } else if (error.type === 'networkError' || error.message?.includes('network') || error.message?.includes('CORS')) {
        errorMessage = '网络请求失败：可能是 CORS 跨域问题。请检查服务器是否正确配置了 CORS 头（Access-Control-Allow-Origin 等）'
      }

      ElMessage({
        message: errorMessage,
        type: 'error',
        duration: 8000
      })
    })

    // 监听 video 元素的错误事件，阻止错误冒泡
    if (videoRef.value) {
      const videoErrorHandler = (e) => {
        e.preventDefault()
        e.stopPropagation()
      }
      videoRef.value.addEventListener('error', videoErrorHandler, true)
    }

    // 初始化播放器并加载视频
    // 第三个参数 false 表示不自动播放（避免需要用户交互的问题）
    try {
      player.initialize(videoRef.value, videoUrl.value, false)

      // 延迟显示成功消息，确保初始化完成
      setTimeout(() => {
        ElMessage({
          message: '视频加载成功',
          type: 'success',
          duration: 2000
        })
      }, 500)

      isInitializing.value = false
      retryCount.value = 0
      return true
    } catch (initError) {
      isInitializing.value = false
      throw new Error(`播放器初始化失败: ${initError.message || '未知错误'}`)
    }
  } catch (error) {
    isInitializing.value = false
    const errorMsg = error.message || error.toString() || '未知错误'

    // 如果是可重试的错误，尝试重试
    if (retryCount.value < maxRetries && (
        errorMsg.includes('Script error') ||
        errorMsg.includes('network') ||
        errorMsg.includes('CORS')
    )) {
      retryCount.value++
      setTimeout(() => {
        ElMessage({
          message: `初始化失败，正在重试 (${retryCount.value}/${maxRetries})...`,
          type: 'warning',
          duration: 2000
        })
        initPlayer()
      }, 1000 * retryCount.value) // 递增延迟
      return false
    }

    ElMessage({
      message: `初始化播放器失败: ${errorMsg}`,
      type: 'error',
      duration: 5000
    })
    return false
  }
}

// 组件挂载时初始化播放器
onMounted(() => {

  // 等待 DOM 和 video 元素完全准备好
  const tryInit = () => {
    if (!videoRef.value) {
      // 如果 video 元素还没准备好，再等一会儿
      setTimeout(tryInit, 100)
      return
    }

    // 尝试初始化
    initPlayer()
  }

  // 确保 DOM 已更新后再初始化
  setTimeout(tryInit, 200)
})

// 组件卸载前清理播放器
onBeforeUnmount(() => {
  // 移除全局错误监听
  if (globalErrorHandler) {
    window.removeEventListener('error', globalErrorHandler, true)
    globalErrorHandler = null
  }
  if (globalRejectionHandler) {
    window.removeEventListener('unhandledrejection', globalRejectionHandler)
    globalRejectionHandler = null
  }

  // 清理播放器
  if (playerRef.value) {
    try {
      playerRef.value.destroy()
    } catch (error) {
      // 忽略销毁时的错误
    }
    playerRef.value = null
  }
})
</script>

<template>
  <div class="page-container">
    <el-form :model="formData" label-width="120px">
      <el-form-item label="dash视频">
        <el-button
            type="primary"
            @click="handleUpload"
            :loading="uploading"
        >
          {{ uploading ? `上传中 ${uploadProgress}%` : '点击上传' }}
        </el-button>
        <span v-if="formData.dashVideoId" class="video-id">
          视频ID: {{ formData.dashVideoId }}
        </span>
      </el-form-item>
    </el-form>

    <div class="video-container">
      <video
          ref="videoRef"
          class="dash-video"
          controls
          playsinline
          preload="auto"
          crossorigin="anonymous"
      ></video>
    </div>
  </div>
</template>

<style scoped>
.page-container {
  padding: 20px;
}

.el-form {
  margin-bottom: 20px;
}

.video-id {
  margin-left: 15px;
  color: #409eff;
  font-size: 14px;
}

.video-container {
  width: 100%;
  max-width: 1200px;
  margin: 20px auto;
  padding: 20px;
  background-color: #000;
  border-radius: 8px;
  /* 使用 aspect-ratio 保持容器比例，避免加载后尺寸变化 */
  aspect-ratio: 16 / 9;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dash-video {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  background-color: #000;
  border-radius: 4px;
  /* 保持视频宽高比，避免尺寸跳动 */
  object-fit: contain;
  display: block;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .video-container {
    padding: 10px;
    margin: 10px;
  }
}
</style>
